import { computed, inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { AdminApproval } from '@trovata/app/shared/models/admin-approval.model';
import { lastValueFrom } from 'rxjs';
import { Workflow, WorkflowStage, WorkflowStageRule, WorkflowToCreateOrUpdate, WorkflowToDelete } from '@trovata/app/features/payments/models/workflow.model';
import {
	CreateWorkflow,
	DeleteWorkflow,
	GetWorkflowById,
	PostWorkflowAdminApproval,
	UpdateWorkflow,
} from '@trovata/app/features/payments/store/actions/workflows.actions';
import { WorkflowsState } from '@trovata/app/features/payments/store/state/workflows/workflow.state';
import { IdentityState } from '@trovata/app/features/settings/store/state/identity.state';
import { CurrentUser } from '@trovata/app/features/settings/models/identity.model';

/**
 * WorkflowsFacadeService - Simplified API over workflows NGXS state management.
 *
 * This facade encapsulates all interactions with WorkflowsState, providing:
 * - Reactive signals for state access (read-only)
 * - Async methods for API operations that update state
 * - Synchronous snapshot methods for immediate state reads
 *
 * Key behaviors:
 * - All state mutations go through NGXS actions
 * - Signals provide reactive, automatically-updating state access
 * - API methods return updated data after state is patched
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowsFacadeService {
	// ==========================================
	// Services (injected)
	// ==========================================

	private readonly store: Store = inject(Store);

	// ==========================================
	// Store Signals
	// ==========================================

	/** Current user from identity state */
	private readonly currentUser: Signal<CurrentUser | undefined> = this.store.selectSignal(IdentityState.currentUser);

	/** Workflows from store */
	private readonly workflowsSignal: Signal<Workflow[] | null> = this.store.selectSignal(WorkflowsState.workflows);

	/** Workflows loading state */
	private readonly workflowsLoading: Signal<boolean> = this.store.selectSignal(WorkflowsState.workflowsLoading);

	// ==========================================
	// Computed Signals
	// ==========================================

	/**
	 * Indicates whether the current user is assigned to ANY active workflow stage.
	 * Checks both stage-level approvals and rule-level approvals.
	 * Returns null while loading, false if not in any workflows, true if assigned.
	 */
	readonly isUserInActiveWorkflowStages: Signal<boolean | null> = computed(() => {
		const workflows: Workflow[] | null = this.workflowsSignal();
		const user: CurrentUser | undefined = this.currentUser();
		const isLoading: boolean = this.workflowsLoading();

		if (isLoading || workflows === null || !user) {
			return null; // Still loading
		}

		const userId: string = user.userId;
		return this.checkUserInActiveWorkflows(workflows, userId);
	});

	// ==========================================
	// Snapshot Methods (synchronous store reads)
	// ==========================================

	/**
	 * Gets a workflow from store by ID without API call.
	 *
	 * @param workflowId - The workflow ID to look up
	 * @returns Workflow if found, undefined otherwise
	 */
	getWorkflowFromStore(workflowId: string): Workflow | undefined {
		const workflows: Workflow[] = this.store.selectSnapshot(WorkflowsState.workflows);
		return workflows?.find((workflow: Workflow) => workflow.workflowId === workflowId);
	}

	// ==========================================
	// Workflow API Methods
	// ==========================================

	/**
	 * Fetches a single workflow from API by ID and updates store.
	 *
	 * @param workflowId - The workflow ID to fetch
	 * @returns Promise resolving to the Workflow, or undefined if not found
	 */
	async getWorkflowFromApi(workflowId: string): Promise<Workflow | undefined> {
		await lastValueFrom(this.store.dispatch(new GetWorkflowById(workflowId)));
		return this.getWorkflowFromStore(workflowId);
	}

	// ==========================================
	// Workflow CRUD Methods
	// ==========================================

	/**
	 * Creates a new workflow.
	 *
	 * @param workflowToCreateOrUpdate - Workflow data for creation
	 */
	async createWorkflow(workflowToCreateOrUpdate: WorkflowToCreateOrUpdate): Promise<void> {
		await lastValueFrom(this.store.dispatch(new CreateWorkflow(workflowToCreateOrUpdate)));
	}

	/**
	 * Updates an existing workflow and returns the updated workflow from store.
	 *
	 * @param workflowToCreateOrUpdate - Workflow data for update (must include workflowId)
	 * @returns Updated Workflow entity from store
	 */
	async updateWorkflow(workflowToCreateOrUpdate: WorkflowToCreateOrUpdate): Promise<Workflow | undefined> {
		await lastValueFrom(this.store.dispatch(new UpdateWorkflow(workflowToCreateOrUpdate)));
		return this.getWorkflowFromStore(workflowToCreateOrUpdate.workflowId);
	}

	/**
	 * Deletes a workflow.
	 *
	 * @param workflowToDelete - Contains workflowId of workflow to delete
	 */
	async deleteWorkflow(workflowToDelete: WorkflowToDelete): Promise<void> {
		await lastValueFrom(this.store.dispatch(new DeleteWorkflow(workflowToDelete)));
	}

	/**
	 * Posts admin approval for a workflow.
	 *
	 * @param adminApproval - Admin approval data
	 */
	async postWorkflowAdminApproval(adminApproval: AdminApproval): Promise<void> {
		await lastValueFrom(this.store.dispatch(new PostWorkflowAdminApproval(adminApproval)));
	}

	// ==========================================
	// Private Methods
	// ==========================================

	/**
	 * Checks if user is in any active workflow stage.
	 *
	 * @param workflows - Array of all workflows
	 * @param userId - The user ID to check
	 * @returns true if user is in any active workflow stage
	 */
	private checkUserInActiveWorkflows(workflows: readonly Workflow[], userId: string): boolean {
		return workflows.some((workflow: Workflow) => workflow.active && this.isUserInWorkflow(workflow, userId));
	}

	/**
	 * Checks if user is in any stage of a workflow.
	 *
	 * @param workflow - The workflow to check
	 * @param userId - The user ID to check
	 * @returns true if user is in any stage of the workflow
	 */
	private isUserInWorkflow(workflow: Workflow, userId: string): boolean {
		return workflow.stages?.some((stage: WorkflowStage) => this.isUserInStage(stage, userId)) ?? false;
	}

	/**
	 * Checks if user is in stage approval or any rule approval.
	 *
	 * @param stage - The workflow stage to check
	 * @param userId - The user ID to check
	 * @returns true if user is in stage approval or any rule approval
	 */
	private isUserInStage(stage: WorkflowStage, userId: string): boolean {
		// Check stage-level approval
		if (stage.approval?.userIds?.includes(userId)) {
			return true;
		}
		// Check rule-level approvals (conditional approvals by amount)
		return stage.rules?.some((rule: WorkflowStageRule) => rule.approval?.userIds?.includes(userId)) ?? false;
	}
}
